import { parseJsonWithNativeSafeNumbers, stringifyLosslessJson } from '@anchorbase/pillar-sdk';
import { useId, useMemo, useState, type ReactElement } from 'react';
import { X } from 'lucide-react';
import { z } from 'zod';
import type {
    JsonValue,
    SearchFieldPredicate,
    SearchFieldPredicateValue,
    SearchJsonPathPredicate,
} from '@anchorbase/pillar-sdk';
import { JsonValueSchema } from '@anchorbase/pillar-sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RemotePredicateMultiselect } from '@/components/ui/remote-predicate-multiselect';
import type {
    InfiniteScrollListFieldPredicateDescriptor,
    InfiniteScrollListPhysicalJsonStructurePredicateDescriptor,
    InfiniteScrollListRemoteFieldPredicateOption,
} from '@/components/ui/infinite-scroll-list.types';

const NoFieldValue = '__no_field_predicate__';
const NoJsonFieldValue = '__no_json_path_predicate__';

export const RemotePredicateValueSchema: z.ZodString = z.string();

export interface ListPredicateControlsValue {
    readonly fieldPredicates: ReadonlyArray<SearchFieldPredicate>;
    readonly jsonPathPredicates: ReadonlyArray<SearchJsonPathPredicate>;
}

export interface ListPredicateControlsProps {
    readonly fieldPredicateDescriptors?: ReadonlyArray<
        InfiniteScrollListFieldPredicateDescriptor
    >;
    readonly jsonPathPredicateDescriptors?: ReadonlyArray<
        InfiniteScrollListPhysicalJsonStructurePredicateDescriptor
    >;
    readonly onChange: (value: ListPredicateControlsValue) => void;
}

interface PredicateChipProps {
    readonly label: string;
    readonly removeLabel: string;
    readonly onRemove: () => void;
}

interface SelectedRemoteOptionLabel {
    readonly field: string;
    readonly value: string;
    readonly label: string;
}

function PredicateChip({
    label,
    removeLabel,
    onRemove,
}: PredicateChipProps): ReactElement {
    return (
        <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-1 text-xs font-medium text-blue-900">
            <span>{label}</span>
            <button
                type="button"
                aria-label={removeLabel}
                className="inline-flex h-4 w-4 items-center justify-center rounded-full hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={onRemove}
            >
                <X className="h-3 w-3" aria-hidden="true" />
            </button>
        </span>
    );
}

function parseTypedValue(
    rawValue: string,
    descriptor: InfiniteScrollListFieldPredicateDescriptor
): SearchFieldPredicateValue {
    if (descriptor.valueType === 'number') {
        const parsedNumber = Number(rawValue);
        if (!Number.isFinite(parsedNumber)) {
            throw new Error(`Invalid numeric predicate value: ${rawValue}`);
        }
        return parsedNumber;
    }
    if (descriptor.valueType === 'boolean') {
        return rawValue === 'true';
    }
    return rawValue;
}

function parseJsonValue(rawValue: string): JsonValue {
    const trimmedValue = rawValue.trim();
    if (trimmedValue.length === 0) {
        return '';
    }
    try {
        return JsonValueSchema.parse(parseJsonWithNativeSafeNumbers(trimmedValue));
    } catch {
        return trimmedValue;
    }
}

function splitJsonPath(rawValue: string): string[] {
    return rawValue
        .split('.')
        .map((pathPart: string) => pathPart.trim())
        .filter((pathPart: string) => pathPart.length > 0);
}

export function ListPredicateControls({
    fieldPredicateDescriptors = [],
    jsonPathPredicateDescriptors = [],
    onChange,
}: ListPredicateControlsProps): ReactElement | null {
    const [fieldPredicates, setFieldPredicates] = useState<
        ReadonlyArray<SearchFieldPredicate>
    >([]);
    const [jsonPathPredicates, setJsonPathPredicates] = useState<
        ReadonlyArray<SearchJsonPathPredicate>
    >([]);
    const [selectedField, setSelectedField] = useState<string>(NoFieldValue);
    const [fieldValueInput, setFieldValueInput] = useState<string>('');
    const [selectedRemoteOptionLabels, setSelectedRemoteOptionLabels] =
        useState<ReadonlyArray<SelectedRemoteOptionLabel>>([]);
    const [selectedJsonField, setSelectedJsonField] =
        useState<string>(NoJsonFieldValue);
    const [jsonPathInput, setJsonPathInput] = useState<string>('');
    const [jsonValueInput, setJsonValueInput] = useState<string>('');
    const physicalJsonHelpId = useId();

    const selectedFieldDescriptor:
        | InfiniteScrollListFieldPredicateDescriptor
        | undefined = useMemo(
        () =>
            fieldPredicateDescriptors.find(
                (descriptor) => descriptor.field === selectedField
            ),
        [fieldPredicateDescriptors, selectedField]
    );
    const selectedJsonDescriptor:
        | InfiniteScrollListPhysicalJsonStructurePredicateDescriptor
        | undefined = useMemo(
        () => {
            if (jsonPathPredicateDescriptors.length === 1) {
                return jsonPathPredicateDescriptors[0];
            }
            return jsonPathPredicateDescriptors.find(
                (descriptor) => descriptor.field === selectedJsonField
            );
        },
        [jsonPathPredicateDescriptors, selectedJsonField]
    );

    const publish = (
        nextFieldPredicates: ReadonlyArray<SearchFieldPredicate>,
        nextJsonPathPredicates: ReadonlyArray<SearchJsonPathPredicate>
    ): void => {
        onChange({
            fieldPredicates: nextFieldPredicates,
            jsonPathPredicates: nextJsonPathPredicates,
        });
    };

    const setFieldPredicate = (
        predicate: SearchFieldPredicate | null,
        field: string
    ): void => {
        const nextFieldPredicates = [
            ...fieldPredicates.filter((currentPredicate) => {
                return currentPredicate.field !== field;
            }),
            ...(predicate === null ? [] : [predicate]),
        ];
        setFieldPredicates(nextFieldPredicates);
        publish(nextFieldPredicates, jsonPathPredicates);
    };

    const addJsonPathPredicate = (): void => {
        if (selectedJsonDescriptor === undefined) {
            return;
        }
        const path = splitJsonPath(jsonPathInput);
        if (path.length === 0) {
            return;
        }
        const nextPredicate: SearchJsonPathPredicate = {
            field: selectedJsonDescriptor.field,
            path,
            operator: 'eq',
            value: parseJsonValue(jsonValueInput),
        };
        const nextJsonPathPredicates = [
            ...jsonPathPredicates,
            nextPredicate,
        ];
        setJsonPathPredicates(nextJsonPathPredicates);
        setJsonPathInput('');
        setJsonValueInput('');
        publish(fieldPredicates, nextJsonPathPredicates);
    };

    const addFreeFormFieldPredicate = (): void => {
        if (selectedFieldDescriptor === undefined) {
            return;
        }
        if (fieldValueInput.trim().length === 0) {
            setFieldPredicate(null, selectedFieldDescriptor.field);
            return;
        }

        const typedValue = parseTypedValue(fieldValueInput, selectedFieldDescriptor);
        setFieldPredicate(
            selectedFieldDescriptor.operator === 'eq'
                ? {
                      field: selectedFieldDescriptor.field,
                      operator: 'eq',
                      value: typedValue,
                  }
                : (() => {
                      const currentPredicate = fieldPredicates.find(
                          (predicate) =>
                              predicate.field === selectedFieldDescriptor.field
                      );
                      const currentValues =
                          currentPredicate?.operator === 'in'
                              ? currentPredicate.values ?? []
                              : [];
                      const nextValues = currentValues.some(
                          (currentValue) => currentValue === typedValue
                      )
                          ? currentValues
                          : [...currentValues, typedValue];
                      return {
                          field: selectedFieldDescriptor.field,
                          operator: 'in',
                          values: nextValues,
                      };
                  })(),
            selectedFieldDescriptor.field
        );
        setFieldValueInput('');
    };

    const setInFieldPredicateValue = (
        descriptor: InfiniteScrollListFieldPredicateDescriptor,
        value: SearchFieldPredicateValue,
        selected: boolean
    ): void => {
        if (!selected && descriptor.remoteOptions !== undefined) {
            setSelectedRemoteOptionLabels((currentLabels) =>
                currentLabels.filter(
                    (currentLabel) =>
                        currentLabel.field !== descriptor.field ||
                        currentLabel.value !== value
                )
            );
        }
        const currentPredicate = fieldPredicates.find(
            (predicate) => predicate.field === descriptor.field
        );
        const currentValues =
            currentPredicate?.operator === 'in'
                ? currentPredicate.values ?? []
                : [];
        const nextValues = selected
            ? currentValues.some((currentValue) => currentValue === value)
                ? currentValues
                : [...currentValues, value]
            : currentValues.filter((currentValue) => currentValue !== value);
        setFieldPredicate(
            nextValues.length === 0
                ? null
                : {
                      field: descriptor.field,
                      operator: 'in',
                      values: nextValues,
                  },
            descriptor.field
        );
    };

    const setRemoteFieldPredicateValue = (
        descriptor: InfiniteScrollListFieldPredicateDescriptor,
        option: InfiniteScrollListRemoteFieldPredicateOption,
        selected: boolean
    ): void => {
        if (selected) {
            setSelectedRemoteOptionLabels((currentLabels) => [
                ...currentLabels.filter(
                    (currentLabel) =>
                        currentLabel.field !== descriptor.field ||
                        currentLabel.value !== option.value
                ),
                {
                    field: descriptor.field,
                    value: option.value,
                    label: option.label,
                },
            ]);
        }
        setInFieldPredicateValue(descriptor, option.value, selected);
    };

    const removeJsonPathPredicate = (index: number): void => {
        const nextJsonPathPredicates = jsonPathPredicates.filter((_, currentIndex) => {
            return currentIndex !== index;
        });
        setJsonPathPredicates(nextJsonPathPredicates);
        publish(fieldPredicates, nextJsonPathPredicates);
    };

    if (
        fieldPredicateDescriptors.length === 0 &&
        jsonPathPredicateDescriptors.length === 0
    ) {
        return null;
    }

    return (
        <div className="flex flex-col gap-3 rounded-md border bg-muted/30 p-3">
            {fieldPredicateDescriptors.length > 0 ? (
                <div className="flex flex-wrap items-center gap-2">
                    <select
                        aria-label="Predicate field"
                        className="h-9 min-w-[220px] rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                        value={selectedField}
                        onChange={(event) => {
                            setSelectedField(event.target.value);
                            setFieldValueInput('');
                        }}
                    >
                        <option value={NoFieldValue}>Field filter</option>
                        {fieldPredicateDescriptors.map((descriptor) => (
                            <option key={descriptor.field} value={descriptor.field}>
                                {descriptor.label}
                            </option>
                        ))}
                    </select>
                    {selectedFieldDescriptor?.remoteOptions !== undefined ? (
                        <RemotePredicateMultiselect
                            key={selectedFieldDescriptor.field}
                            descriptor={selectedFieldDescriptor}
                            selectedValues={fieldPredicates
                                .find(
                                    (predicate) =>
                                        predicate.field ===
                                            selectedFieldDescriptor.field &&
                                        predicate.operator === 'in'
                                )
                                ?.values?.flatMap((value) => {
                                    const parseResult =
                                        RemotePredicateValueSchema.safeParse(value);
                                    return parseResult.success
                                        ? [parseResult.data]
                                        : [];
                                }) ?? []}
                            onSelectionChange={(option, selected) =>
                                setRemoteFieldPredicateValue(
                                    selectedFieldDescriptor,
                                    option,
                                    selected
                                )
                            }
                        />
                    ) : selectedFieldDescriptor?.operator === 'in' &&
                    (selectedFieldDescriptor.options?.length ?? 0) > 0 ? (
                        <details className="relative">
                            <summary
                                aria-label={`${selectedFieldDescriptor.label} values`}
                                className="flex h-9 min-w-[220px] cursor-pointer list-none items-center rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                            >
                                Select {selectedFieldDescriptor.label}
                            </summary>
                            <div className="absolute right-0 z-20 mt-1 min-w-[320px] rounded-md border bg-background p-2 shadow-md">
                                <Input
                                    aria-label={`Search ${selectedFieldDescriptor.label}`}
                                    className="mb-2 h-9"
                                    placeholder={`Search ${selectedFieldDescriptor.label.toLowerCase()}...`}
                                    value={fieldValueInput}
                                    onChange={(event) => {
                                        setFieldValueInput(event.target.value);
                                    }}
                                />
                                <div className="max-h-72 overflow-y-auto">
                                {selectedFieldDescriptor.options
                                    ?.filter((option) =>
                                        option.label
                                            .toLocaleLowerCase()
                                            .includes(
                                                fieldValueInput
                                                    .trim()
                                                    .toLocaleLowerCase()
                                            )
                                    )
                                    .map((option) => {
                                    const currentPredicate = fieldPredicates.find(
                                        (predicate) =>
                                            predicate.field ===
                                            selectedFieldDescriptor.field
                                    );
                                    const selected =
                                        currentPredicate?.operator === 'in' &&
                                        (currentPredicate.values ?? []).some(
                                            (value) => value === option.value
                                        );
                                    return (
                                        <label
                                            key={`${selectedFieldDescriptor.field}:${String(option.value)}`}
                                            className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted"
                                        >
                                            <input
                                                type="checkbox"
                                                aria-label={`${selectedFieldDescriptor.label}: ${option.label}`}
                                                checked={selected}
                                                onChange={(event) =>
                                                    setInFieldPredicateValue(
                                                        selectedFieldDescriptor,
                                                        option.value,
                                                        event.target.checked
                                                    )
                                                }
                                            />
                                            <span>{option.label}</span>
                                        </label>
                                    );
                                })}
                                </div>
                            </div>
                        </details>
                    ) : selectedFieldDescriptor !== undefined &&
                      (selectedFieldDescriptor.options?.length ?? 0) > 0 ? (
                        <select
                            aria-label={`${selectedFieldDescriptor.label} value`}
                            className="h-9 min-w-[220px] rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                            value=""
                            onChange={(event) => {
                                const selectedValue = event.target.value;
                                if (selectedValue.length === 0) {
                                    setFieldPredicate(
                                        null,
                                        selectedFieldDescriptor.field
                                    );
                                    return;
                                }
                                const typedValue = parseTypedValue(
                                    selectedValue,
                                    selectedFieldDescriptor
                                );
                                setFieldPredicate(
                                    selectedFieldDescriptor.operator === 'eq'
                                        ? {
                                              field: selectedFieldDescriptor.field,
                                              operator: 'eq',
                                              value: typedValue,
                                          }
                                        : (() => {
                                              const currentPredicate =
                                                  fieldPredicates.find(
                                                      (predicate) =>
                                                          predicate.field ===
                                                          selectedFieldDescriptor.field
                                                  );
                                              const currentValues =
                                                  currentPredicate?.operator === 'in'
                                                      ? currentPredicate.values ?? []
                                                      : [];
                                              const nextValues = currentValues.some(
                                                  (currentValue) =>
                                                      currentValue === typedValue
                                              )
                                                  ? currentValues
                                                  : [...currentValues, typedValue];
                                              return {
                                                  field: selectedFieldDescriptor.field,
                                                  operator: 'in',
                                                  values: nextValues,
                                              };
                                          })(),
                                    selectedFieldDescriptor.field
                                );
                            }}
                        >
                            <option value="">All values</option>
                            {selectedFieldDescriptor.options?.map((option) => (
                                <option
                                    key={`${selectedFieldDescriptor.field}:${String(option.value)}`}
                                    value={String(option.value)}
                                >
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    ) : selectedFieldDescriptor !== undefined ? (
                        <>
                            <Input
                                aria-label={`${selectedFieldDescriptor.label} value`}
                                className="h-9 w-[220px]"
                                placeholder={`${selectedFieldDescriptor.label} value`}
                                value={fieldValueInput}
                                onChange={(event) =>
                                    setFieldValueInput(event.target.value)
                                }
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                        event.preventDefault();
                                        addFreeFormFieldPredicate();
                                    }
                                }}
                            />
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={addFreeFormFieldPredicate}
                            >
                                Add Filter
                            </Button>
                        </>
                    ) : null}
                    <div className="flex flex-wrap gap-2">
                        {fieldPredicates.flatMap((predicate) => {
                            const descriptor = fieldPredicateDescriptors.find(
                                (candidate) => candidate.field === predicate.field
                            );
                            if (
                                predicate.operator === 'in' &&
                                ((descriptor?.options?.length ?? 0) > 0 ||
                                    descriptor?.remoteOptions !== undefined)
                            ) {
                                return (predicate.values ?? []).map((value) => {
                                    const optionLabel =
                                        descriptor?.options?.find(
                                            (option) => option.value === value
                                        )?.label ??
                                        selectedRemoteOptionLabels.find(
                                            (selectedOption) =>
                                                selectedOption.field ===
                                                    predicate.field &&
                                                selectedOption.value === value
                                        )?.label ??
                                        String(value);
                                    return (
                                        <PredicateChip
                                            key={`${predicate.field}:${String(value)}`}
                                            label={`${descriptor?.label ?? predicate.field}: ${optionLabel}`}
                                            removeLabel={`Remove ${optionLabel} from ${descriptor?.label ?? predicate.field} filter`}
                                            onRemove={() =>
                                                descriptor === undefined
                                                    ? setFieldPredicate(
                                                          null,
                                                          predicate.field
                                                      )
                                                    : setInFieldPredicateValue(
                                                          descriptor,
                                                          value,
                                                          false
                                                      )
                                            }
                                        />
                                    );
                                });
                            }
                            return [
                                <PredicateChip
                                    key={predicate.field}
                                    label={`${predicate.field}: ${
                                        predicate.operator === 'eq'
                                            ? String(predicate.value)
                                            : predicate.values?.map(String).join(', ') ?? ''
                                    }`}
                                    removeLabel={`Remove ${predicate.field} filter`}
                                    onRemove={() =>
                                        setFieldPredicate(null, predicate.field)
                                    }
                                />,
                            ];
                        })}
                    </div>
                </div>
            ) : null}

            {jsonPathPredicateDescriptors.length > 0 ? (
                <div className="flex flex-col gap-2">
                    <div>
                        <p className="text-sm font-medium">
                            Physical JSON structure
                        </p>
                        <p
                            id={physicalJsonHelpId}
                            className="text-xs text-muted-foreground"
                        >
                            Match one path in the stored JSON exactly. Use the
                            path from the record&apos;s stored version; paths are
                            not translated between versions.
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        {jsonPathPredicateDescriptors.length > 1 ? (
                            <select
                                aria-label="Physical JSON field"
                                aria-describedby={physicalJsonHelpId}
                                className="h-9 min-w-[220px] rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                value={selectedJsonField}
                                onChange={(event) =>
                                    setSelectedJsonField(event.target.value)
                                }
                            >
                                <option value={NoJsonFieldValue}>
                                    Stored JSON field
                                </option>
                                {jsonPathPredicateDescriptors.map((descriptor) => (
                                    <option
                                        key={descriptor.field}
                                        value={descriptor.field}
                                    >
                                        {descriptor.label}
                                    </option>
                                ))}
                            </select>
                        ) : null}
                        <Input
                            aria-label="Physical JSON path"
                            aria-describedby={physicalJsonHelpId}
                            className="h-9 w-[220px]"
                            placeholder="exact.path.in.stored.version"
                            value={jsonPathInput}
                            onChange={(event) =>
                                setJsonPathInput(event.target.value)
                            }
                        />
                        <Input
                            aria-label="Physical JSON value"
                            aria-describedby={physicalJsonHelpId}
                            className="h-9 w-[220px]"
                            placeholder="JSON value"
                            value={jsonValueInput}
                            onChange={(event) =>
                                setJsonValueInput(event.target.value)
                            }
                        />
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            aria-label="Add Physical JSON Filter"
                            aria-describedby={physicalJsonHelpId}
                            onClick={addJsonPathPredicate}
                            disabled={selectedJsonDescriptor === undefined}
                        >
                            Add Physical JSON Filter
                        </Button>
                        <div className="flex flex-wrap gap-2">
                            {jsonPathPredicates.map((predicate, index) => {
                                const descriptorLabel =
                                    jsonPathPredicateDescriptors.find(
                                        (descriptor) =>
                                            descriptor.field === predicate.field
                                    )?.label ?? predicate.field;
                                return (
                                    <PredicateChip
                                        key={`${predicate.field}:${predicate.path.join('.')}:${index.toString()}`}
                                        label={`${descriptorLabel} · ${predicate.path.join('.')}: ${stringifyLosslessJson(predicate.value)}`}
                                        removeLabel={`Remove ${descriptorLabel} physical JSON filter`}
                                        onRemove={() =>
                                            removeJsonPathPredicate(index)
                                        }
                                    />
                                );
                            })}
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
